import { ArrowRight, ChevronDown, ChevronRight, Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { EnrichedFinding, FindingStatus } from '../../types/data-integrity.js';
import {
  SeverityBadge,
  StatusBadge,
  STATUS_LABEL_KEYS,
  STATUS_TRANSITIONS,
} from './IntegrityBadges.js';

/** @graph-connects none */
export function FindingRow({
  finding,
  onExclude,
  onRestore,
  onStatusChange,
}: {
  finding: EnrichedFinding;
  onExclude: (f: EnrichedFinding) => void;
  onRestore: (f: EnrichedFinding) => void;
  onStatusChange: (f: EnrichedFinding, newStatus: FindingStatus) => void;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const transitions = STATUS_TRANSITIONS[finding.operationalStatus];

  return (
    <div
      className={`border-b border-[var(--color-border)] ${finding.excluded ? 'opacity-40' : ''}`}
    >
      <div className="flex items-center gap-3 px-4 py-3">
        <button
          type="button"
          onClick={() => {
            setExpanded(!expanded);
          }}
          className="shrink-0 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
        >
          {expanded ? (
            <ChevronDown className="h-4 w-4" aria-hidden="true" />
          ) : (
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          )}
        </button>
        <SeverityBadge severity={finding.severity} />
        <StatusBadge status={finding.operationalStatus} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-[var(--color-accent-primary)]">
              {finding.category}
            </span>
            <span className="text-xs text-[var(--color-text-muted)]">{finding.entityLabel}</span>
          </div>
          <p className="mt-0.5 text-sm text-[var(--color-text-primary)] line-clamp-1">
            {finding.description}
          </p>
        </div>
        <div className="shrink-0 text-right">
          <div className="font-mono text-xs text-[var(--color-text-muted)]">
            {finding.recordId.length > 12
              ? `${finding.recordId.slice(0, 12)}...`
              : finding.recordId}
          </div>
          <div className="text-xs text-[var(--color-text-muted)]">
            {t('dataIntegrity.ageElapsed', {
              value:
                finding.ageHours < 1
                  ? t('dataIntegrity.ageUnderOneHour')
                  : finding.ageHours < 24
                    ? `${String(finding.ageHours)}h`
                    : `${String(Math.floor(finding.ageHours / 24))}d`,
            })}
          </div>
        </div>
        <div className="shrink-0">
          {finding.excluded ? (
            <button
              type="button"
              onClick={() => {
                onRestore(finding);
              }}
              className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-[var(--color-text-muted)] hover:bg-[var(--color-bg-elevated)]"
            >
              <Eye className="h-3 w-3" aria-hidden="true" />
              {t('dataIntegrity.restore')}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                onExclude(finding);
              }}
              className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-[var(--color-text-muted)] hover:bg-[var(--color-bg-elevated)]"
            >
              <EyeOff className="h-3 w-3" aria-hidden="true" />
              {t('dataIntegrity.exclude')}
            </button>
          )}
        </div>
      </div>

      {expanded && (
        <div className="ml-11 mr-4 mb-3 rounded-lg bg-[var(--color-bg-elevated)] p-4 text-sm space-y-3">
          <div>
            <span className="font-medium text-[var(--color-text-primary)]">
              {t('dataIntegrity.recordId')}:{' '}
            </span>
            <span className="font-mono text-[var(--color-text-secondary)]">{finding.recordId}</span>
          </div>
          <div>
            <span className="font-medium text-[var(--color-text-primary)]">
              {t('dataIntegrity.direction')}:{' '}
            </span>
            <span className="text-[var(--color-text-secondary)]">
              {finding.direction === 'pg_only'
                ? t('dataIntegrity.directionPgOnly')
                : t('dataIntegrity.directionMongoOnly')}
            </span>
          </div>
          {finding.createdAt && (
            <div>
              <span className="font-medium text-[var(--color-text-primary)]">
                {t('dataIntegrity.createdAt')}:{' '}
              </span>
              <span className="text-[var(--color-text-secondary)]">
                {new Date(finding.createdAt).toLocaleString('ja-JP')}
              </span>
            </div>
          )}
          <div className="pt-2 border-t border-[var(--color-border)]">
            <span className="font-medium text-[var(--color-text-primary)]">
              {t('dataIntegrity.impact')}:{' '}
            </span>
            <span className="text-[var(--color-text-secondary)]">{finding.impact}</span>
          </div>
          <div className="rounded border border-[var(--color-accent-primary)]/20 bg-[var(--color-accent-primary)]/5 p-3">
            <span className="font-medium text-[var(--color-accent-primary)]">
              {t('dataIntegrity.recommendedAction')}:{' '}
            </span>
            <span className="text-[var(--color-text-primary)]">{finding.action}</span>
          </div>
          {finding.excluded && finding.exclusionReason && (
            <div className="text-xs text-[var(--color-text-muted)]">
              {t('dataIntegrity.exclusionReason')}: {finding.exclusionReason}
            </div>
          )}
          {!finding.excluded && transitions.length > 0 && (
            <div className="pt-2 border-t border-[var(--color-border)]">
              <div className="text-xs font-medium text-[var(--color-text-muted)] mb-2">
                {t('dataIntegrity.changeStatus')}:
              </div>
              <div className="flex flex-wrap gap-2">
                {transitions.map((nextStatus) => (
                  <button
                    key={nextStatus}
                    type="button"
                    onClick={() => {
                      onStatusChange(finding, nextStatus);
                    }}
                    className="inline-flex items-center gap-1 rounded border border-[var(--color-border)] px-2 py-1 text-xs text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)]"
                  >
                    <ArrowRight className="h-3 w-3" aria-hidden="true" />
                    {t(STATUS_LABEL_KEYS[nextStatus])}
                  </button>
                ))}
              </div>
            </div>
          )}
          {finding.statusUpdatedBy && (
            <div className="text-xs text-[var(--color-text-muted)]">
              {t('common.lastUpdated')}: {finding.statusUpdatedBy}
              {finding.statusUpdatedAt &&
                ` (${new Date(finding.statusUpdatedAt).toLocaleString('ja-JP')})`}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
